/**
 * In-memory log of Nectar analytics events sent to the server, for the dev console.
 *
 * The analytics hook calls `recordBatch` at its single network choke point (gated by
 * `isNectarConsoleEnabled()`), and the console subscribes via `useSyncExternalStore`.
 * This module holds no React dependency. It is active in local dev and wherever the
 * `VITE_SHOW_NECTAR_CONSOLE` env var is enabled (e.g. staging).
 */
import type { AnalyticsEvent } from '~/hooks/useNectarAnalytics';

export interface NectarEventLogEntry {
  /** Wall-clock time the batch was captured (ms). */
  loggedAt: number;
  /** Shared id for every event that went out in the same POST. */
  batchId: string;
  event: AnalyticsEvent;
}

const MAX_ENTRIES = 200;

let entries: NectarEventLogEntry[] = [];
let nextBatchId = 0;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

/**
 * Record a batch of events that was just sent to the server. Newest entries are
 * kept at the front; the log is capped at MAX_ENTRIES (oldest dropped).
 */
export const recordBatch = (events: AnalyticsEvent[]): void => {
  if (events.length === 0) return;

  const loggedAt = Date.now();
  const batchId = `batch_${nextBatchId++}`;
  const newEntries = events.map((event) => ({ loggedAt, batchId, event }));

  entries = [...newEntries.reverse(), ...entries].slice(0, MAX_ENTRIES);
  emit();
};

export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSnapshot = (): NectarEventLogEntry[] => entries;

export const clear = (): void => {
  if (entries.length === 0) return;
  entries = [];
  emit();
};
